import '../operations.css';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  Filter,
  Mail,
  Monitor,
  MousePointer,
  QrCode,
  RefreshCw,
  Search,
  ShieldAlert,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { Table } from '../../components/ui/Table';
import { campaignsApi } from '../../api/campaigns';
import type { CampaignOccurrence, QRCodeEntry } from '../../api/campaigns';
import { templatesApi } from '../../api/templates';
import type { CampaignEvent } from '../../types';

export function CampaignResults() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const campaignId = Number(id);

  const { data: campaign, isError: campaignError } = useQuery({
    queryKey: ['campaign', id],
    queryFn: async () => {
      const res = await campaignsApi.getById(campaignId);
      return res.data;
    },
    enabled: Number.isFinite(campaignId),
  });

  const { data: resultsPayload } = useQuery({
    queryKey: ['campaign-results', id],
    queryFn: async () => {
      const res = await campaignsApi.getResults(campaignId);
      return res.data;
    },
    enabled: Number.isFinite(campaignId),
  });
  const results = useMemo(() => resultsPayload?.results ?? [], [resultsPayload]);
  const timeline = useMemo(() => resultsPayload?.timeline ?? [], [resultsPayload]);

  // Stats live on CampaignSummary, not the full Campaign object.
  const { data: summary } = useQuery({
    queryKey: ['campaign-summary', id],
    queryFn: async () => {
      const res = await campaignsApi.getCampaignSummary(campaignId);
      return res.data;
    },
    enabled: Number.isFinite(campaignId),
  });

  // Load the series' occurrences when this campaign is a recurring parent or
  // itself a spawned occurrence.
  const isSeriesMember = Boolean(campaign?.recurring || (campaign?.parent_campaign_id ?? 0) > 0);
  const { data: occurrences } = useQuery({
    queryKey: ['campaign-occurrences', id],
    queryFn: async () => {
      const res = await campaignsApi.getOccurrences(campaignId);
      return res.data;
    },
    enabled: isSeriesMember,
  });

  const isQRChannel = campaign?.channel === 'qr';
  const { data: qrCodes } = useQuery({
    queryKey: ['campaign-qr-codes', id],
    queryFn: async () => {
      const res = await campaignsApi.getQRCodes(campaignId);
      return res.data;
    },
    enabled: isQRChannel && Number.isFinite(campaignId),
  });

  // Template name lookup so each result can show which variant it used.
  // Only fetched when the campaign actually has variants or results carry
  // a template_id.
  const hasVariants =
    (campaign?.template_ids?.length ?? 0) > 0 ||
    (results ?? []).some((r) => (r.template_id ?? 0) > 0);
  const { data: templates } = useQuery({
    queryKey: ['templates'],
    queryFn: async () => {
      const res = await templatesApi.getAll();
      return res.data;
    },
    enabled: hasVariants,
  });
  const templateNames = new Map<number, string>(
    (templates ?? []).map((t) => [t.id, t.name]),
  );
  const eventsByEmail = useMemo(() => groupEvents(timeline), [timeline]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showQr, setShowQr] = useState(false);
  const [qrPage, setQrPage] = useState(1);
  const [showExportConfirm, setShowExportConfirm] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return results.filter((result) => {
      if (statusFilter !== 'all' && result.status !== statusFilter) return false;
      if (!query) return true;
      const name = `${result.first_name} ${result.last_name}`.toLowerCase();
      return result.email.toLowerCase().includes(query) || name.includes(query);
    });
  }, [results, search, statusFilter]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const visible = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const qrList = qrCodes ?? [];
  const qrPageCount = Math.max(1, Math.ceil(qrList.length / pageSize));
  const qrCurrent = Math.min(qrPage, qrPageCount);
  const visibleQr = qrList.slice((qrCurrent - 1) * pageSize, qrCurrent * pageSize);

  if (campaignError) {
    return (
      <div className="flex items-center justify-center h-64">
        <p role="alert" className="text-red-700">Could not load this campaign.</p>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-gray-500">Loading...</div>
      </div>
    );
  }

  const stats = summary?.stats;
  const total = stats?.total ?? results.length;

  return (
    <div className="operations-page campaign-results-page space-y-6">
      <header className="campaign-results-header">
        <Link to="/campaigns" className="campaign-results-back" aria-label="Back to campaigns">
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </Link>
        <div className="campaign-results-title">
          <p className="operations-eyebrow">Simulation workspace</p>
          <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
          <p className="campaign-results-subtitle">
            {campaign.channel === 'email' && campaign.smtp?.name
              ? `Email results sent with ${campaign.smtp.name}`
              : 'Campaign results and analytics'}
            {campaign.recurring && (
              <span className="badge inline-flex items-center gap-1 ml-2 bg-indigo-50 text-indigo-700">
                <RefreshCw className="w-3 h-3" />
                Recurring{campaign.recurrence_interval ? ` · ${campaign.recurrence_interval}` : ''}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn-secondary campaign-export-button"
            onClick={() => {
              void queryClient.invalidateQueries({ queryKey: ['campaign', id] });
              void queryClient.invalidateQueries({ queryKey: ['campaign-results', id] });
              void queryClient.invalidateQueries({ queryKey: ['campaign-summary', id] });
              void queryClient.invalidateQueries({ queryKey: ['campaign-occurrences', id] });
              void queryClient.invalidateQueries({ queryKey: ['campaign-qr-codes', id] });
            }}
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button type="button" className="btn-primary campaign-export-button" onClick={() => { setExportError(''); setShowExportConfirm(true); }}>
            <Download className="w-4 h-4" />
            Download CSV
          </button>
        </div>
      </header>

      <div className="campaign-metrics" aria-label="Campaign performance">
        <ResultMetric
          title="Emails Sent"
          value={stats?.sent ?? 0}
          icon={<Mail className="w-5 h-5" />}
          detail={total > 0 ? `${percentage(stats?.sent ?? 0, total)}% of recipients` : 'No recipients'}
          tone="neutral"
        />
        <ResultMetric
          title="Opened"
          value={stats?.opened ?? 0}
          icon={<Eye className="w-5 h-5" />}
          detail={`${percentage(stats?.opened ?? 0, total)}% open rate`}
          tone="info"
        />
        <ResultMetric
          title="Clicked"
          value={stats?.clicked ?? 0}
          icon={<MousePointer className="w-5 h-5" />}
          detail={`${percentage(stats?.clicked ?? 0, total)}% click rate`}
          tone="warning"
        />
        <ResultMetric
          title="Submitted"
          value={stats?.submitted_data ?? 0}
          icon={<AlertTriangle className="w-5 h-5" />}
          detail={`${percentage(stats?.submitted_data ?? 0, total)}% submission rate`}
          tone="danger"
        />
        <ResultMetric
          title="Reported"
          value={stats?.email_reported ?? 0}
          icon={<ShieldAlert className="w-5 h-5" />}
          detail={`${percentage(stats?.email_reported ?? 0, total)}% report rate`}
          tone="success"
        />
      </div>

      {isQRChannel && (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <QrCode className="w-5 h-5" /> QR Codes
            </h3>
            <button type="button" className="btn-secondary px-3 py-1.5 text-sm" onClick={() => setShowQr((open) => !open)}>
              {showQr ? 'Hide QR codes' : `Show QR codes (${qrList.length})`}
            </button>
          </div>
          {showQr && (
            <>
              <p className="text-sm text-gray-500 mt-4 mb-4">
                Each recipient has a unique QR code that links to their tracked landing-page URL.
              </p>
              {visibleQr.length === 0 ? (
                <p className="text-sm text-gray-500">No QR codes generated yet. Launch the campaign first.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {visibleQr.map((qr: QRCodeEntry) => (
                    <a
                      key={qr.rid}
                      href={qr.data_uri}
                      download={`qr-${qr.rid}.png`}
                      className="text-center group"
                      title={`Download QR for ${qr.email}`}
                    >
                      <img
                        src={qr.data_uri}
                        alt={`QR code for ${qr.email}`}
                        className="w-full h-auto border rounded-lg group-hover:shadow-md transition-shadow"
                      />
                      <p className="text-xs text-gray-600 mt-2 truncate">{qr.email}</p>
                    </a>
                  ))}
                </div>
              )}
              {qrList.length > pageSize && (
                <Pager page={qrCurrent} pageCount={qrPageCount} onPage={setQrPage} />
              )}
            </>
          )}
        </Card>
      )}

      {isSeriesMember && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Recurring series</h3>
          {campaign.recurring && isRealDate(campaign.next_run_at) && (
            <p className="text-sm text-gray-500 mb-4">
              Next occurrence launches {new Date(campaign.next_run_at!).toLocaleString()}
            </p>
          )}
          <Table
            columns={occurrenceColumns}
            data={occurrences ?? []}
            keyExtractor={(o) => o.id}
            onRowClick={(o) => navigate(`/campaigns/${o.id}`)}
            emptyMessage={
              campaign.recurring
                ? 'No occurrences yet. The next one is scheduled automatically.'
                : 'No sibling occurrences in this series.'
            }
          />
        </Card>
      )}

      <Card>
        <div className="recipient-table-heading">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Recipients</h3>
            <p className="text-sm text-gray-500">{filtered.length} of {results.length} employees</p>
          </div>
          <div className="recipient-toolbar">
            <label className="recipient-search">
              <Search className="w-4 h-4" aria-hidden="true" />
              <span className="sr-only">Search recipients</span>
            <input
              aria-label="Search recipients"
                placeholder="Search name or email"
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(1); }}
            />
            </label>
            <label className="recipient-select">
              <Filter className="w-4 h-4" aria-hidden="true" />
              <span className="sr-only">Filter by status</span>
            <select
              aria-label="Status"
              value={statusFilter}
              onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }}
            >
              <option value="all">All</option>
              <option value="Email Sent">Sent</option>
              <option value="Email Opened">Opened</option>
              <option value="Clicked Link">Clicked</option>
              <option value="Submitted Data">Submitted</option>
              <option value="Reported">Reported</option>
              <option value="Error">Error</option>
            </select>
            </label>
            <label className="recipient-select recipient-page-size">
              <span className="text-sm text-gray-500">Rows</span>
            <select
              aria-label="Rows per page"
              value={pageSize}
              onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            </label>
          </div>
        </div>
        {visible.length === 0 ? (
          <p className="text-sm text-gray-500">No results yet</p>
        ) : (
          <div className="table-scroll overflow-x-auto">
            <table className="data-table w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Recipient</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Last activity</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Activity</th>
                  <th className="px-4 py-3"><span className="sr-only">Details</span></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((result) => {
                  const open = expandedId === result.id;
                  const events = eventsByEmail.get(result.email.toLowerCase()) ?? [];
                  return (
                    <Fragment key={result.id}>
                      <tr
                        className={`recipient-row ${open ? 'is-expanded' : ''}`}
                        onClick={() => setExpandedId(open ? null : result.id)}
                        aria-expanded={open}
                      >
                        <td className="px-4 py-3">
                          <p className="recipient-email">{result.email}</p>
                          <p className="text-sm text-gray-500">{result.first_name} {result.last_name}</p>
                        </td>
                        <td className="px-4 py-3"><span className={`badge ${getStatusBadge(result.status)}`}>{result.status}</span></td>
                        <td className="px-4 py-3 text-sm text-gray-500">{latestActivity(events, result.send_date)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{events.length} event{events.length === 1 ? '' : 's'} · {statusHint(result.status)}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            className="recipient-expand"
                            aria-label={`${open ? 'Hide' : 'Show'} details for ${result.email}`}
                            onClick={(event) => { event.stopPropagation(); setExpandedId(open ? null : result.id); }}
                          >
                            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                      </tr>
                      {open && (
                        <tr className="recipient-detail-row">
                          <td colSpan={5}>
                            <RecipientDetail
                              events={events}
                              variant={(result.template_id ?? 0) > 0 ? templateNames.get(result.template_id!) ?? `#${result.template_id}` : ''}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Pager page={currentPage} pageCount={pageCount} onPage={setPage} />
      </Card>

      <ExportConfirmation
        open={showExportConfirm}
        exporting={isExporting}
        error={exportError}
        recipientCount={results.length}
        onClose={() => { if (!isExporting) setShowExportConfirm(false); }}
        onConfirm={() => void downloadCampaignCSV(campaignId, campaign.name, setIsExporting, setExportError, setShowExportConfirm)}
      />
    </div>
  );
}

const occurrenceColumns = [
  {
    key: 'launch_date',
    header: 'Launch Date',
    render: (occurrence: CampaignOccurrence) => (
      <span className="text-sm text-gray-700">
        {new Date(occurrence.launch_date).toLocaleString()}
      </span>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (occurrence: CampaignOccurrence) => (
      <span className={`badge ${getCampaignStatusBadge(occurrence.status)}`}>
        {occurrence.status}
      </span>
    ),
  },
  {
    key: 'stats',
    header: 'Results',
    render: (occurrence: CampaignOccurrence) => (
      <div className="text-sm">
        <span className="text-green-600">{occurrence.stats?.sent ?? 0} sent</span>
        <span className="mx-1">|</span>
        <span className="text-blue-600">{occurrence.stats?.clicked ?? 0} clicked</span>
        <span className="mx-1">|</span>
        <span className="text-red-600">{occurrence.stats?.submitted_data ?? 0} submitted</span>
      </div>
    ),
  },
];

// isRealDate guards against the Go zero time ("0001-01-01T00:00:00Z"), which
// the backend emits for recurring series with no further scheduled runs.
function isRealDate(value?: string | null): boolean {
  if (!value) return false;
  const year = new Date(value).getFullYear();
  return Number.isFinite(year) && year > 2000;
}

function percentage(value: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((value / total) * 100);
}

function ResultMetric({
  title,
  value,
  icon,
  detail,
  tone,
}: {
  title: string;
  value: number;
  icon: ReactNode;
  detail: string;
  tone: 'neutral' | 'info' | 'warning' | 'danger' | 'success';
}) {
  return (
    <Card className={`campaign-metric metric-${tone}`}>
      <div className="campaign-metric-icon">{icon}</div>
      <div>
        <p className="campaign-metric-label">{title}</p>
        <p className="campaign-metric-value">{value}</p>
        <p className="campaign-metric-detail">{detail}</p>
      </div>
    </Card>
  );
}

function latestActivity(events: CampaignEvent[], fallback: string): string {
  const value = events.length > 0 ? events[events.length - 1].time : fallback;
  if (!value) return 'Not available';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not available' : date.toLocaleString();
}

function getCampaignStatusBadge(status: string): string {
  switch (status) {
    case 'Completed':
    case 'Emails Sent':
      return 'badge-green';
    case 'In progress':
      return 'badge-yellow';
    default:
      return 'badge-gray';
  }
}

function parseEventDetails(raw?: string): { payload?: Record<string, string[] | string>; browser?: Record<string, string> } | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function payloadEntries(payload?: Record<string, string[] | string>): [string, string][] {
  if (!payload) return [];
  return Object.entries(payload)
    .filter(([key]) => key.toLowerCase() !== 'rid')
    .map(([key, value]) => [key, Array.isArray(value) ? value.join(', ') : String(value)]);
}

function browserField(browser: Record<string, string>, key: string, label: string) {
  const value = browser[key];
  if (!value) return null;
  return (
    <div key={key}>
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900 break-all">{value}</dd>
    </div>
  );
}

function groupEvents(timeline: CampaignEvent[]) {
  const grouped = new Map<string, CampaignEvent[]>();
  for (const event of timeline) {
    const key = event.email.toLowerCase();
    const list = grouped.get(key);
    if (list) list.push(event);
    else grouped.set(key, [event]);
  }
  return grouped;
}

function statusHint(status: string) {
  switch (status) {
    case 'Email Sent': return 'sent';
    case 'Email Opened': return 'opened';
    case 'Clicked Link': return 'clicked';
    case 'Submitted Data': return 'submitted';
    case 'Reported': return 'reported';
    case 'Error': return 'error';
    default: return status;
  }
}

function Pager({ page, pageCount, onPage }: { page: number; pageCount: number; onPage: (page: number) => void }) {
  return (
    <div className="flex items-center justify-end gap-3 mt-4 text-sm">
      <button type="button" className="btn-secondary px-3 py-1.5" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button>
      <span>Page {page} of {pageCount}</span>
      <button type="button" className="btn-secondary px-3 py-1.5" disabled={page >= pageCount} onClick={() => onPage(page + 1)}>Next</button>
    </div>
  );
}

function RecipientDetail({ events, variant }: { events: CampaignEvent[]; variant: string }) {
  if (events.length === 0) return <p className="text-sm text-gray-500">No tracked events yet.</p>;
  const parsedEvents = events.map((event) => ({ event, details: parseEventDetails(event.details) }));
  const browser = parsedEvents.reduce<Record<string, string>>((combined, item) => ({
    ...combined,
    ...(item.details?.browser ?? {}),
  }), {});
  const submissions = parsedEvents.filter(({ event }) => event.message === 'Submitted Data');
  return (
    <div className="recipient-detail">
      <div className="recipient-detail-topline">
        <div>
          <p className="recipient-detail-kicker">Recipient intelligence</p>
          <h4>Complete activity record</h4>
        </div>
        {variant && <span className="badge badge-gray">Template: {variant}</span>}
      </div>

      <div className="recipient-detail-grid">
        <section className="recipient-detail-section">
          <div className="recipient-detail-section-title">
            <Activity className="w-4 h-4" />
            <h5>Activity timeline</h5>
          </div>
          <ol className="recipient-timeline">
            {parsedEvents.map(({ event }, index) => (
              <li key={`${event.time}-${index}`}>
                <span className={`recipient-timeline-dot ${event.message === 'Submitted Data' ? 'is-danger' : ''}`} />
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="font-medium text-gray-900">{event.message}</p>
              <time className="text-sm text-gray-500">{new Date(event.time).toLocaleString()}</time>
            </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="recipient-detail-section">
          <div className="recipient-detail-section-title">
            <Monitor className="w-4 h-4" />
            <h5>Device and browser</h5>
          </div>
          <dl className="recipient-facts">
            {browserField(browser, 'address', 'IP address')}
            {browserField(browser, 'device_type', 'Device')}
            {browserField(browser, 'email_client', 'Mail client')}
            {browserField(browser, 'platform', 'Platform')}
            {browserField(browser, 'language', 'Language')}
            {browserField(browser, 'timezone', 'Timezone')}
            {browserField(browser, 'screen', 'Screen')}
            {browserField(browser, 'user-agent', 'Browser')}
          </dl>
          {Object.keys(browser).length === 0 && <p className="recipient-detail-empty">No device details were recorded.</p>}
        </section>
      </div>

      {submissions.length > 0 && (
        <section className="submitted-data-panel">
          <div className="submitted-data-heading">
            <span className="submitted-data-icon"><ShieldAlert className="w-5 h-5" /></span>
            <div>
              <p className="recipient-detail-kicker">Sensitive simulation evidence</p>
              <h5>Submitted data</h5>
              <p>Values captured from this recipient’s landing-page submission.</p>
            </div>
          </div>
          <div className="submitted-data-events">
            {submissions.map(({ event, details }, index) => {
              const payload = payloadEntries(details?.payload);
              return (
                <article key={`${event.time}-${index}`} className="submitted-data-event">
                  <time>{new Date(event.time).toLocaleString()}</time>
                  {payload.length > 0 ? (
                    <dl className="submitted-data-values">
                      {payload.map(([key, value]) => (
                        <div key={key}>
                          <dt>{key}</dt>
                          <dd>{value}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : (
                    <p className="recipient-detail-empty">No form fields were captured for this submit.</p>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

function ExportConfirmation({
  open,
  exporting,
  error,
  recipientCount,
  onClose,
  onConfirm,
}: {
  open: boolean;
  exporting: boolean;
  error: string;
  recipientCount: number;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal isOpen={open} onClose={onClose} title="Download complete campaign report" size="sm">
      <div className="export-warning">
        <ShieldAlert className="w-6 h-6" />
        <div>
          <h3>Sensitive data warning</h3>
          <p>
            This CSV contains all {recipientCount} employees, their activity and device details,
            and every captured form value, including passwords when present.
          </p>
        </div>
      </div>
      <p className="export-guidance">Store the file securely and share it only with authorized awareness administrators.</p>
      {error && <p className="export-error" role="alert">{error}</p>}
      <div className="export-actions">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={exporting}>Cancel</button>
        <button type="button" className="btn-primary" onClick={onConfirm} disabled={exporting}>
          <Download className="w-4 h-4" />
          {exporting ? 'Preparing report…' : 'Download full report'}
        </button>
      </div>
    </Modal>
  );
}

async function downloadCampaignCSV(
  campaignId: number,
  campaignName: string,
  setExporting: (value: boolean) => void,
  setError: (value: string) => void,
  setOpen: (value: boolean) => void,
) {
  setExporting(true);
  setError('');
  try {
    const response = await campaignsApi.exportResultsCSV(campaignId);
    const disposition = String(response.headers['content-disposition'] ?? '');
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const safeName = campaignName.trim().replace(/[^a-z0-9_-]+/gi, '-').replace(/^-|-$/g, '') || `campaign-${campaignId}`;
    const filename = match?.[1] ?? `${safeName}-results.csv`;
    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    setOpen(false);
  } catch {
    setError('The campaign report could not be downloaded. Please try again.');
  } finally {
    setExporting(false);
  }
}

function getStatusBadge(status: string): string {
  switch (status) {
    case 'Email Sent':
      return 'badge-gray';
    case 'Email Opened':
      return 'badge-yellow';
    case 'Clicked Link':
      return 'badge-red';
    case 'Submitted Data':
      return 'badge-red';
    case 'Reported':
      return 'badge-green';
    case 'Error':
      return 'badge-red';
    default:
      return 'badge-gray';
  }
}
